import React from "react";
import { Link } from "react-router-dom";

/**
 * The public front page.
 *
 * Self-contained styling — its own tokens, always light — because a
 * marketing page shouldn't flip to dark mode because a visitor's laptop
 * says so, and it's the one page seen by people who've never signed in.
 *
 * The screenshots are the real interface: same markup, same colours, same
 * scorecard conventions. Replace them with captures of real rounds before
 * this goes anywhere commercial. A board with actual names on it does more
 * work than any mock.
 */

const N = "#0A2240";
const R = "#D6001C";

/* ---------- the marks ---------- */

const FullMark = ({ h = 40, ink = N, halo = "#fff" }) => (
  <svg viewBox="2 15 60 43" height={h} width={h * (60 / 43)} aria-hidden="true">
    <rect x="5" y="18" width="42" height="29" rx="2.5" fill="none" stroke={ink} strokeWidth="3.4" />
    <path d="M5 27h42" stroke={ink} strokeWidth="3" />
    <path d="M24 27v20" stroke={ink} strokeWidth="3" />
    <circle cx="46" cy="42" r="14.5" fill={halo} />
    <circle cx="46" cy="42" r="12.5" fill="none" stroke={ink} strokeWidth="3.2" />
    <line x1="42.5" y1="35.5" x2="42.5" y2="48.5" stroke={ink} strokeWidth="2.7" strokeLinecap="round" />
    <path d="M42.5 35 L52 38.3 L42.5 41.6 Z" fill={R} />
  </svg>
);

const Frank = ({ s = 28, ink = N }) => (
  <svg viewBox="30 26 32 32" width={s} height={s} aria-hidden="true">
    <circle cx="46" cy="42" r="12.5" fill="none" stroke={ink} strokeWidth="3.2" />
    <line x1="42.5" y1="35.5" x2="42.5" y2="48.5" stroke={ink} strokeWidth="2.7" strokeLinecap="round" />
    <path d="M42.5 35 L52 38.3 L42.5 41.6 Z" fill={R} />
  </svg>
);

const Word = ({ size = "1.15rem", ink = N }) => (
  <span style={{ fontSize: size, fontWeight: 700, letterSpacing: "-.025em", color: ink,
                 lineHeight: 1, whiteSpace: "nowrap" }}>
    PostcardG<span style={{ color: R }}>o</span>lf
  </span>
);

/* ---------- the screenshots, drawn from the real interface ---------- */

/* ---------- the screenshots ----------
 *
 * Every figure below is real: it comes from the demo data in
 * db/demo/demo_data.sql, which builds cards around a target gross of par
 * plus course handicap. Round 3 of the Wayfarers Cup at Burnham & Berrow,
 * par 70, as the app itself would show it.
 *
 * Stableford points are worked out here from par and stroke index rather
 * than pasted in, so the mock can't quietly disagree with the app.
 */

const PAR = [4,3,4,4,5,4,3,4,4, 4,3,4,4,4,4,5,3,4];
const SI  = [5,11,17,1,7,13,15,3,9, 6,12,18,10,4,14,16,8,2];

// Will Grant, off 15 from the yellows: 84 gross, 69 nett, 37 points
const CARD = [5,5,5,4,5,4,3,5,6, 6,5,4,4,5,4,5,4,5];
const PH = 15;

const shotsOn = (i) => (SI[i] <= PH % 18 ? 1 : 0) + Math.floor(PH / 18);
const pointsOn = (i) => Math.max(0, 2 + (PAR[i] + shotsOn(i)) - CARD[i]);
const sum = (a, f, s, e) => a.slice(s, e).reduce((t, _, i) => t + f(i + s), 0);

const board = [
  { pos: 1, name: "Niall Deane",    tee: "White",  hcp: 14, gross: 79,  nett: 65, pts: 41 },
  { pos: 2, name: "Jen Whitlock",   tee: "Red",    hcp: 24, gross: 91,  nett: 67, pts: 39 },
  { pos: 3, name: "Will Grant",     tee: "Yellow", hcp: 15, gross: 84,  nett: 69, pts: 37, me: true },
  { pos: 4, name: "Helen Marsh",    tee: "Red",    hcp: 24, gross: 94,  nett: 70, pts: 36, tie: true },
  { pos: 4, name: "Barry Coombes",  tee: "Yellow", hcp: 31, gross: 101, nett: 70, pts: 37, tie: true },
  { pos: 6, name: "Colin Fry",      tee: "Yellow", hcp: 29, gross: 100, nett: 71, pts: 36 },
  { pos: 7, name: "Gareth Vaughan", tee: "White",  hcp: 10, gross: 82,  nett: 72, pts: 34 },
  { pos: 8, name: "Sue Ballantyne", tee: "Red",    hcp: 29, gross: 102, nett: 73, pts: 33 },
];

const mark = (s, par) => {
  const d = s - par;
  return d <= -2 ? "mk-eagle" : d === -1 ? "mk-birdie"
       : d === 0 ? "mk-par" : d === 1 ? "mk-bogey" : "mk-double";
};

const Leaderboard = () => (
  <div className="shot">
    <div className="shot-bar"><Frank s={18} ink="#fff" /><b>Wayfarers</b><span>Wayfarers Cup · R3 of 4</span></div>
    <div className="shot-body">
      <div className="shot-tabs"><b>Nett</b><span>Gross</span><span>Stableford</span></div>
      <table className="lb-mini">
        <thead><tr><th>Pos</th><th>Player</th><th>Hcp</th><th>Gross</th><th>Pts</th><th>Nett</th></tr></thead>
        <tbody>
          {board.map((r, i) => (
            <tr key={i} className={r.me ? "me" : undefined}>
              <td className={r.pos === 1 ? "gold" : undefined}>{r.tie ? "T" : ""}{r.pos}</td>
              <td className="nm">{r.name}<small>{r.tee} tees</small></td>
              <td className="dim">{r.hcp}</td>
              <td className="dim">{r.gross}</td>
              <td className="dim">{r.pts}</td>
              <td className="key">{r.nett}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="nr-mini">
        <span><b>Tom Ridley</b> walked in after 12 — no nett or gross placing,
        Stableford points still count</span>
        <i>NR</i>
      </div>
    </div>
  </div>
);

const Nine = ({ from, to, label }) => (
  <>
    <tr className="hole">
      <th>Hole</th>
      {PAR.slice(from, to).map((_, i) => <th key={i}>{from + i + 1}</th>)}
      <th className="tot">{label}</th>
    </tr>
    <tr><td>Par</td>
      {PAR.slice(from, to).map((p, i) => <td key={i} className="par">{p}</td>)}
      <td className="tot">{PAR.slice(from, to).reduce((a, b) => a + b, 0)}</td></tr>
    <tr><td>Index</td>
      {SI.slice(from, to).map((x, i) => <td key={i} className="dim">{x}</td>)}
      <td className="tot">—</td></tr>
    <tr><td>Shots</td>
      {SI.slice(from, to).map((_, i) => (
        <td key={i} className={shotsOn(from + i) ? "shot-dot" : "dim"}>
          {shotsOn(from + i) ? "•" : "–"}
        </td>))}
      <td className="tot">—</td></tr>
    <tr><td>Score</td>
      {CARD.slice(from, to).map((c, i) => (
        <td key={i}><span className={`mk ${mark(c, PAR[from + i])}`}>{c}</span></td>))}
      <td className="tot">{CARD.slice(from, to).reduce((a, b) => a + b, 0)}</td></tr>
    <tr><td>Points</td>
      {CARD.slice(from, to).map((_, i) => (
        <td key={i} className="dim">{pointsOn(from + i)}</td>))}
      <td className="tot">{sum(CARD, pointsOn, from, to)}</td></tr>
  </>
);

const Scorecard = () => (
  <div className="shot">
    <div className="shot-bar"><Frank s={18} ink="#fff" /><b>Will Grant</b>
      <span>Burnham &amp; Berrow · off 15</span></div>
    <div className="shot-body">
      <table className="sc-mini"><tbody>
        <Nine from={0} to={9} label="Out" />
        <tr className="gap"><td colSpan={11} /></tr>
        <Nine from={9} to={18} label="In" />
      </tbody></table>
      <div className="totals-mini">
        <div><span>Gross</span><b>84</b></div>
        <div><span>Nett</span><b>69</b></div>
        <div><span>Points</span><b>37</b></div>
      </div>
      <div className="legend-mini">
        <span><i className="mk mk-eagle">3</i> Eagle or better</span>
        <span><i className="mk mk-birdie">3</i> Birdie</span>
        <span><i className="mk mk-bogey">5</i> Bogey</span>
        <span><i className="mk mk-double">6</i> Double or worse</span>
        <span style={{ color: R }}>• Shot received</span>
      </div>
    </div>
  </div>
);

const Phone = () => (
  <div className="phone">
    <div className="phone-bar"><Frank s={15} ink="#fff" /><b>Wayfarers</b></div>
    <div className="phone-body">
      <div className="eyebrow">Your card · Round 3</div>
      <div className="phone-hero">
        <div className="big">69<span>nett</span></div>
        <div className="split">
          <div><span>Gross</span><b>84</b></div>
          <div><span>Hcp</span><b>15</b></div>
          <div><span>Points</span><b>37</b></div>
        </div>
      </div>
      <div className="eyebrow" style={{ marginTop: "0.9rem" }}>Leaderboard · 3rd of 15</div>
      <div className="phone-rows">
        {board.slice(0, 4).map((r, i) => (
          <div key={i} className={`phone-row${r.me ? " me" : ""}`}>
            <b>{r.tie ? "T" : ""}{r.pos}</b><span>{r.name}</span><i>{r.nett}</i>
          </div>
        ))}
      </div>
      <div className="phone-cta">Enter my card</div>
    </div>
  </div>
);

/* ---------- content ---------- */

const FEATURES = [
  { t: "Scores that add up",
    d: "Only strokes are stored. Nett, Stableford and gross are worked out from them, so correcting a stroke index months later re-does every card on that course rather than leaving history wrong." },
  { t: "Handicaps done properly",
    d: "Index frozen when a player enters, course handicap derived per round from slope and rating, playing handicap after allowance. Mixed tees in the same round are fine." },
  { t: "Everyone plays, not just the app users",
    d: "Members don't need an account. Organisers add them, enter their cards and they appear on the board like anyone else — which matters when half your society won't install anything." },
  { t: "Multi-round competitions",
    d: "An order of merit over four rounds, best two of three, or a single Saturday. Countback resolves ties the way the R&A says: last nine, last six, last three, last hole." },
  { t: "Three boards, not one",
    d: "Nett, gross and Stableford are each a real leaderboard with its own countback, not a re-sort of the same numbers. Tap any player for their card and their scoring by hole length." },
  { t: "No-returns handled",
    d: "A card that wasn't holed out keeps its Stableford points and drops out of the nett and gross placings, instead of quietly sitting at the top on nine holes." },
];

export default function Landing() {
  return (
    <div className="landing">
      <style>{CSS}</style>

      <header className="nav">
        <div className="wrap nav-in">
          <span className="brand"><Frank s={24} /><Word /></span>
          <span className="spacer" />
          <Link to="/signin" className="btn btn-dark">Sign in</Link>
        </div>
      </header>

      <section className="hero">
        <div className="wrap hero-in">
          <div className="hero-text">
            <span className="kicker">Golf society scoring</span>
            <h1>Post your card.<br />See where you stand.</h1>
            <p>
              A scoring system for golf societies — the kind with a fixture
              list, a handicap secretary and a few members who'd rather not
              install anything. Cards go in after the round, the board works
              itself out, and nobody argues in the bar about countback.
            </p>
            <div className="hero-cta">
              <Link to="/signin" className="btn btn-red">Sign in</Link>
              <a href="#what" className="btn btn-ghost-light">See what it does</a>
            </div>
            <p className="hero-note">
              Free while it's in the hands of a few societies. Accounts are by
              invitation from an organiser.
            </p>
          </div>
          <div className="hero-shot"><Phone /></div>
        </div>
      </section>

      <section className="strip">
        <div className="wrap strip-in">
          {[["16", "members, one society"], ["4", "rounds, best three count"],
            ["3", "boards per round"], ["0", "apps to install"]].map(([n, l]) => (
            <div key={l}><b>{n}</b><span>{l}</span></div>
          ))}
        </div>
      </section>

      <section className="section" id="what">
        <div className="wrap">
          <span className="kicker dark">The leaderboard</span>
          <h2>A board that looks like a board</h2>
          <p className="lead">
            Modelled on a championship scoreboard rather than a spreadsheet.
            Tap a name and their card opens underneath it — every round they've
            played, their scoring by hole length, and where they stand. A card
            that wasn't holed out keeps its points and drops out of the nett
            and gross placings, rather than sitting at the top on twelve holes.
          </p>
          <div className="shot-frame"><Leaderboard /></div>
        </div>
      </section>

      <section className="section alt">
        <div className="wrap">
          <span className="kicker dark">The card</span>
          <h2>The card your members already know</h2>
          <p className="lead">
            Red circle under par, black square over, exactly as it appears on
            television. The shots row shows where the handicap gives a stroke,
            so a nett total is something anyone can check rather than a number
            the app asserts.
          </p>
          <div className="shot-frame"><Scorecard /></div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <span className="kicker dark">What it does</span>
          <h2>Built for how societies actually work</h2>
          <div className="grid">
            {FEATURES.map((f) => (
              <div className="card" key={f.t}>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section alt">
        <div className="wrap">
          <span className="kicker dark">Getting going</span>
          <h2>An evening's work, then it runs itself</h2>
          <ol className="steps">
            {[["Add your members", "Names and handicap indexes. Email addresses only for the ones who want an account."],
              ["Add a course", "Par and stroke index off a scorecard, once per venue, then reusable forever."],
              ["Set up a competition", "One round or four, nett or Stableford, best two of three if that's your thing."],
              ["Cards in, board out", "Members enter their own, or you type up the paper ones at the bar."]].map(([t, d], i) => (
              <li key={t}><b>{i + 1}</b><div><strong>{t}</strong><span>{d}</span></div></li>
            ))}
          </ol>
        </div>
      </section>

      <section className="closer">
        <div className="wrap closer-in">
          <FullMark h={54} ink="#fff" halo={N} />
          <h2>Run your society's golf properly</h2>
          <p>
            Free while it's with a handful of societies. If yours would like an
            account, ask whoever runs your fixtures.
          </p>
          <Link to="/signin" className="btn btn-red">Sign in</Link>
        </div>
      </section>

      <footer className="foot">
        <div className="wrap foot-in">
          <span className="brand"><Frank s={20} /><Word size="1rem" /></span>
          <span className="spacer" />
          <span className="foot-note">postcardgolf.app</span>
        </div>
      </footer>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap');
.landing { background:#fff; color:${N}; font-family:Barlow, system-ui, sans-serif;
  font-feature-settings:"tnum" 1; }
.landing *, .landing *::before, .landing *::after { box-sizing:border-box; }
.landing .wrap { max-width:1060px; margin:0 auto; padding:0 1.25rem; }
.landing .spacer { flex:1; }
.landing .kicker { display:block; font-size:.625rem; font-weight:700; letter-spacing:.18em;
  text-transform:uppercase; color:rgba(255,255,255,.55); margin-bottom:.6rem; }
.landing .kicker.dark { color:#5C6B7A; }
.landing h1 { font-size:clamp(2.1rem,5.5vw,3.4rem); line-height:1.04; letter-spacing:-.03em;
  margin:0 0 1rem; font-weight:700; }
.landing h2 { font-size:clamp(1.5rem,3.2vw,2.1rem); letter-spacing:-.025em; margin:0 0 .8rem;
  font-weight:700; }
.landing h3 { font-size:1.0625rem; font-weight:700; margin:0 0 .4rem; }
.landing .lead { font-size:1rem; line-height:1.7; color:#3C4C5F; max-width:62ch; margin:0 0 1.6rem; }

.landing .btn { display:inline-block; border-radius:2px; padding:.78rem 1.4rem; cursor:pointer;
  font-size:.9375rem; font-weight:600; text-decoration:none; border:none; }
.landing .btn-red { background:${R}; color:#fff; }
.landing .btn-dark { background:${N}; color:#fff; }
.landing .btn-ghost-light { background:none; color:#fff; border:1px solid rgba(255,255,255,.4); }

.landing .nav { border-bottom:1px solid #DFE3E8; position:sticky; top:0; background:#fff; z-index:10; }
.landing .nav-in { display:flex; align-items:center; gap:1rem;
  padding:.8rem 1.25rem; min-height:60px; }
.landing .brand { display:inline-flex; align-items:center; gap:.4rem; line-height:1; }

.landing .hero { background:${N}; color:#fff; padding:3.5rem 0 4rem; }
.landing .hero-in { display:grid; grid-template-columns:1.15fr .85fr; gap:3rem; align-items:center; }
@media (max-width:860px) { .landing .hero-in { grid-template-columns:1fr; gap:2.25rem; } }
.landing .hero p { font-size:1.0625rem; line-height:1.7; color:rgba(255,255,255,.8); max-width:52ch; margin:0 0 1.6rem; }
.landing .hero-cta { display:flex; gap:.6rem; flex-wrap:wrap; }
.landing .hero-note { font-size:.8125rem !important; color:rgba(255,255,255,.55) !important; margin-top:1.4rem !important; }
.landing .hero-shot { display:flex; justify-content:center; }

.landing .strip { background:#F5F8FA; border-bottom:1px solid #DFE3E8; }
.landing .strip-in { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem;
  padding-top:1.6rem; padding-bottom:1.6rem; text-align:center; }
@media (max-width:640px) { .landing .strip-in { grid-template-columns:repeat(2,1fr); gap:1.4rem; } }
.landing .strip b { display:block; font-size:2rem; font-weight:700; letter-spacing:-.02em; }
.landing .strip span { font-size:.75rem; color:#5C6B7A; }

.landing .section { padding:3.5rem 0; }
.landing .section.alt { background:#F5F8FA; border-top:1px solid #DFE3E8; border-bottom:1px solid #DFE3E8; }

.landing .grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; }
@media (max-width:860px) { .landing .grid { grid-template-columns:repeat(2,1fr); } }
@media (max-width:560px) { .landing .grid { grid-template-columns:1fr; } }
.landing .card { background:#fff; border:1px solid #DFE3E8; border-left:3px solid ${N}; padding:1.1rem 1.2rem; }
.landing .section.alt .card { background:#fff; }
.landing .card p { font-size:.875rem; line-height:1.65; color:#3C4C5F; margin:0; }

.landing .steps { list-style:none; margin:0; padding:0; display:grid; gap:.7rem; }
.landing .steps li { display:flex; gap:.9rem; align-items:flex-start; background:#fff;
  border:1px solid #DFE3E8; padding:1rem 1.15rem; }
.landing .steps b { flex:0 0 auto; width:26px; height:26px; border-radius:50%; background:${R};
  color:#fff; display:grid; place-items:center; font-size:.8125rem; font-weight:700; }
.landing .steps strong { display:block; font-size:.9375rem; }
.landing .steps span { display:block; font-size:.875rem; color:#3C4C5F; line-height:1.6; margin-top:.15rem; }

.landing .closer { background:${N}; color:#fff; padding:3.5rem 0; }
.landing .closer-in { text-align:center; display:flex; flex-direction:column; align-items:center; gap:.9rem; }
.landing .closer p { color:rgba(255,255,255,.75); max-width:46ch; margin:0; line-height:1.7; }

.landing .foot { border-top:1px solid #DFE3E8; padding:1.4rem 0; }
.landing .foot-in { display:flex; align-items:center; }
.landing .foot-note { font-size:.8125rem; color:#5C6B7A; }

/* ---------- the screenshots ---------- */
.landing .shot-frame { border:1px solid #DFE3E8; background:#fff; box-shadow:0 1px 3px rgba(10,34,64,.08);
  overflow:hidden; }
.landing .shot-bar { background:${N}; color:#fff; padding:.6rem .8rem; display:flex;
  align-items:center; gap:.5rem; font-size:.8125rem; }
.landing .shot-bar b { font-weight:700; text-transform:uppercase; letter-spacing:.01em; }
.landing .shot-bar span { margin-left:auto; font-size:.5625rem; letter-spacing:.14em;
  text-transform:uppercase; color:rgba(255,255,255,.5); font-weight:700; }
.landing .shot-body { padding:.9rem; overflow-x:auto; }
.landing .shot-tabs { display:flex; gap:1.1rem; border-bottom:1px solid #DFE3E8; margin-bottom:.7rem;
  font-size:.875rem; color:#5C6B7A; }
.landing .shot-tabs b { color:${N}; border-bottom:3px solid ${R}; padding-bottom:.45rem; margin-bottom:-1px; }
.landing .shot-tabs span { padding-bottom:.45rem; }

.landing .lb-mini { width:100%; border-collapse:collapse; font-size:.875rem; min-width:420px; }
.landing .lb-mini th { background:${N}; color:rgba(255,255,255,.62); padding:.4rem .5rem;
  font-size:.5625rem; font-weight:700; letter-spacing:.14em; text-transform:uppercase; text-align:right; }
.landing .lb-mini th:nth-child(-n+2) { text-align:left; }
.landing .lb-mini td { border-bottom:1px solid #E9EDF1; padding:.5rem; text-align:right; }
.landing .lb-mini td:nth-child(-n+2) { text-align:left; }
.landing .lb-mini tr.me td { background:#FCF3F4; }
.landing .lb-mini tr.me td:first-child { box-shadow:inset 3px 0 0 ${R}; }
.landing .lb-mini .gold { color:#9C7C21; font-weight:700; }
.landing .lb-mini .nm { font-weight:600; }
.landing .lb-mini .nm small { display:block; font-size:.6875rem; color:#5C6B7A; font-weight:400; }
.landing .lb-mini .dim { color:#5C6B7A; font-size:.8125rem; }
.landing .lb-mini .key { font-weight:700; font-size:1.05rem; }

.landing .sc-mini { border-collapse:collapse; width:100%; min-width:640px; font-size:.8125rem; }
.landing .sc-mini th, .landing .sc-mini td { border:1px solid #DFE3E8; text-align:center;
  padding:.3rem .2rem; height:36px; }
.landing .sc-mini tr td:first-child, .landing .sc-mini tr th:first-child { text-align:left;
  padding-left:.6rem; font-size:.625rem; font-weight:700; letter-spacing:.1em;
  text-transform:uppercase; color:#5C6B7A; min-width:72px; }
.landing .sc-mini .hole th { font-weight:700; color:${N}; font-size:.875rem; }
.landing .sc-mini .tot { background:#F5F8FA; font-weight:700; }
.landing .sc-mini .par { color:#2B6CB0; font-weight:600; }
.landing .sc-mini .dim { color:#93A0AE; }
.landing .sc-mini .shot-dot { color:${R}; }
.landing .mk { display:inline-grid; place-items:center; width:24px; height:24px;
  font-weight:700; font-size:.8125rem; }
.landing .mk-birdie { background:${R}; border-radius:50%; color:#fff; }
.landing .mk-eagle { background:${R}; border-radius:50%; color:#fff;
  box-shadow:0 0 0 2px #fff, 0 0 0 4px ${R}; }
.landing .mk-par { color:${N}; }
.landing .mk-bogey { background:#111417; border-radius:2px; color:#fff; }
.landing .mk-double { border:2px solid #111417; border-radius:2px; color:#111417; }
.landing .sc-mini tr.gap td { border:none; height:6px; padding:0; background:#fff; }
.landing .nr-mini { display:flex; align-items:center; gap:.8rem; margin-top:.7rem;
  padding:.6rem .8rem; background:#F5F8FA; border:1px solid #DFE3E8; font-size:.75rem;
  color:#5C6B7A; line-height:1.5; }
.landing .nr-mini b { color:${N}; }
.landing .nr-mini i { font-style:normal; font-weight:700; font-size:.5625rem; letter-spacing:.1em;
  text-transform:uppercase; background:#fff; border:1px solid #DFE3E8; padding:.2rem .45rem;
  color:#5C6B7A; margin-left:auto; }
.landing .totals-mini { display:grid; grid-template-columns:repeat(3,1fr); gap:.5rem;
  margin-top:.7rem; border:1px solid #DFE3E8; padding:.7rem; text-align:center; }
.landing .totals-mini span { display:block; font-size:.5625rem; font-weight:700;
  letter-spacing:.14em; text-transform:uppercase; color:#5C6B7A; }
.landing .totals-mini b { display:block; font-size:1.5rem; font-weight:700; margin-top:.1rem; }
.landing .legend-mini { display:flex; flex-wrap:wrap; gap:.9rem; justify-content:center;
  padding:.7rem; margin-top:.6rem; background:#F5F8FA; border:1px solid #DFE3E8;
  font-size:.75rem; color:#5C6B7A; }
.landing .legend-mini span { display:flex; align-items:center; gap:.35rem; }
.landing .legend-mini .mk { width:18px; height:18px; font-size:.625rem; }

.landing .phone { width:270px; background:#fff; border:9px solid #0C1A2B; border-radius:28px;
  overflow:hidden; box-shadow:0 14px 40px rgba(0,0,0,.35); }
.landing .phone-bar { background:${N}; color:#fff; padding:.55rem .7rem; display:flex;
  align-items:center; gap:.4rem; font-size:.75rem; }
.landing .phone-bar b { font-weight:700; text-transform:uppercase; }
.landing .phone-body { padding:.75rem; background:#fff; color:${N}; }
.landing .phone-body .eyebrow { font-size:.5625rem; font-weight:700; letter-spacing:.16em;
  text-transform:uppercase; color:#5C6B7A; margin-bottom:.35rem; }
.landing .phone-hero { background:${N}; color:#fff; padding:.8rem; }
.landing .phone-hero .big { font-size:2.6rem; font-weight:700; line-height:1; letter-spacing:-.02em; }
.landing .phone-hero .big span { font-size:.8125rem; font-weight:600; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.6); margin-left:.4rem; }
.landing .phone-hero .split { display:grid; grid-template-columns:repeat(3,1fr); gap:.4rem;
  margin-top:.7rem; padding-top:.6rem; border-top:1px solid rgba(255,255,255,.18); }
.landing .phone-hero .split span { display:block; font-size:.5rem; font-weight:700;
  letter-spacing:.14em; text-transform:uppercase; color:rgba(255,255,255,.5); }
.landing .phone-hero .split b { font-size:1rem; font-weight:700; }
.landing .phone-rows { border:1px solid #DFE3E8; background:#fff; }
.landing .phone-row { display:grid; grid-template-columns:1.6rem 1fr 2rem; gap:.4rem;
  align-items:center; padding:.45rem .5rem; border-bottom:1px solid #E9EDF1; font-size:.75rem; }
.landing .phone-row:last-child { border-bottom:none; }
.landing .phone-row.me { background:#FCF3F4; box-shadow:inset 3px 0 0 ${R}; }
.landing .phone-row b { font-weight:700; color:${N}; }
.landing .phone-row span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  font-weight:600; color:${N}; }
.landing .phone-row i { font-style:normal; font-weight:700; text-align:right; color:${N}; }
.landing .phone-cta { background:${R}; color:#fff; text-align:center; padding:.6rem;
  font-size:.8125rem; font-weight:700; margin-top:.7rem; }
`;
